<script setup>
import { useStore } from "vuex";
import { computed, ref, watch } from "vue";
import {
  trackPrintDesignScaled,
  trackBackgroundToggle
} from "components/pod/pod-build-order/utils/tracker";
import ImageReviewMeasurement from "./ImageReviewMeasurement.vue";
import ImageReviewImage from "./ImageReviewImage.vue";
import ImageReviewControls from "./ImageReviewControls.vue";
import ImageReviewEnhancing from "./ImageReviewEnhancing.vue";
import ImageReviewEnhanced from "./ImageReviewEnhanced.vue";
import ImageReviewAsis from "./ImageReviewAsis.vue";
import ImageReviewBgToggle from "./ImageReviewBgToggle.vue";
import ImageReviewTitles from "./ImageReviewTitles.vue";
import ImageReviewRequest from "./ImageReviewRequest.vue";
import ImageReviewLegend from "./imageReviewLegend.vue";

const props = defineProps({
  sideKey: {
    type: String,
    required: true
  },
  titlePrefix: {
    type: String,
    required: true
  }
});

const asisShown = ref(false);
const imageReviewComponent = ref(null);
const scale = ref(1);
const legendShown = ref(true);
const title = computed(() => `${props.titlePrefix} Image Review`);

const store = useStore();
const config = computed(() => store.getters.config);
const artwork = computed(() => store.getters[`artwork_${props.sideKey}/artwork`]);
const artworkSize = computed(() => store.getters[`artwork_${props.sideKey}/artworkSize`]);
const artworkUrl = computed(() => store.getters[`artwork_${props.sideKey}/artworkUrl`]);
const artworkLoading = computed(() => store.getters[`artwork_${props.sideKey}/uploadArtwork`]);
const enhanced = computed(() => store.getters[`artwork_${props.sideKey}/enhanced`]);
const loadingShown = computed(() => artworkLoading.value && enhanced.value);
const enhancedShown = computed(() => artwork.value && !artworkLoading.value && enhanced.value);
const loadingStatus = computed(() => store.getters[`artwork_${props.sideKey}/uploadStatus`]);
const loadingPercentage = computed(() =>
  parseFloat(store.getters[`artwork_${props.sideKey}/uploadProgress`]).toFixed(2)
);
const supportsMade = computed(() => store.getters["app/supportsMade"]);
const artworkExists = computed(() => store.getters[`artwork_${props.sideKey}/artworkExists`]);
const imageUrl = computed(() => store.getters[`artwork_${props.sideKey}/artworkUrl`]);
const comment = computed(() => store.getters[`artwork_${props.sideKey}/comment`]);
const bgRemoved = computed(() => store.getters[`artwork_${props.sideKey}/removeBackground`]);
const toggleShown = computed(
  () => artwork.value?.metadata?.can_remove_bg && enhanced.value && !artworkLoading.value
);
const bgStyle = computed(() => {
  const { color_hex: hex, swatch_url: url } = store.getters["product/currentComposition"];
  return {
    background: url ? `url(${url})` : hex
  };
});
const editType = computed(() => store.getters[`artwork_${props.sideKey}/editType`]);

const currentProduct = computed(() => store.getters["product/currentProduct"]);

const price = computed(() => {
  const { prices } = currentProduct.value;
  try {
    return Object.values(Object.values(prices)[0])[0][0];
  } catch (_) {
    return 0;
  }
});

const oneInch = computed(() => {
  const canvas = imageReviewComponent.value;
  if (!canvas) return 0;
  // 1inch is equal 35% of canvas width. It's a magic number.
  const onInchInPercentage = 35;
  return Math.min(Math.floor((onInchInPercentage * canvas.clientWidth) / 100), 131);
});
const pxToInch = size => store.getters[`surface_${props.sideKey}/convertPxToInches`](size);
const initWidth = computed(() => pxToInch(artworkSize.value.width) * oneInch.value);

const useAsis = () => {
  store.dispatch(`artwork_${props.sideKey}/setArtworkEnhanced`, false);
  asisShown.value = true;
};

const useEnhancement = () => {
  store.dispatch(`artwork_${props.sideKey}/setArtworkEnhanced`, true);
};

const clearChoose = () => {
  store.dispatch(`artwork_${props.sideKey}/setArtworkEnhanced`, false);
  asisShown.value = false;
};

const removeBackgroundChange = e => {
  store.commit(`artwork_${props.sideKey}/setRemoveBackground`, e.target.checked);

  trackBackgroundToggle(artwork.value, price.value, e.target.checked);
};

const updateSize = scaledImageValue => {
  if (!scaledImageValue || !Number.isFinite(scaledImageValue)) return;

  const oldValue = scale.value;
  scale.value = scaledImageValue * oneInch.value;

  if (oldValue !== scale.value && oldValue !== 1) legendShown.value = false;

  trackPrintDesignScaled({
    artwork_url: artworkUrl.value,
    old_scale: oldValue,
    new_scale: scale.value,
    artwork: artwork.value,
    price: price.value,
    editType: editType.value
  });
};

const saveComment = commentVal => {
  store.dispatch(`artwork_${props.sideKey}/setArtworkComment`, commentVal);
};

watch(
  () => artworkExists.value,
  (newValue, oldValue) => {
    // Reset all states when artwork is removed
    if (oldValue && !newValue) {
      asisShown.value = false;
      scale.value = 1;
      legendShown.value = true;
    }
  }
);
</script>

<template>
  <div
    ref="imageReviewComponent"
    class="image-review"
    :class="{ 'image-review--disabled': !artworkExists || !supportsMade }"
  >
    <div class="image-review__title">{{ title }}</div>

    <div class="image-review__background-wrapper">
      <div v-if="artworkExists" data-testid="image-review-main-wrapper">
        <div v-if="enhancedShown" class="image-review__titles" data-testid="image-review-titles">
          <ImageReviewTitles :enhanced="enhanced" />
        </div>

        <div class="image-review__image">
          <ImageReviewMeasurement :one-inch="oneInch" :scale="scale">
            <ImageReviewImage
              :artwork-size="artworkSize"
              :init-width="initWidth"
              :image-url="imageUrl"
              :bg-style="bgStyle"
              :side-key="props.sideKey"
              @update:zoom="updateSize"
            />
          </ImageReviewMeasurement>
        </div>

        <ImageReviewLegend v-if="legendShown" data-testid="image-review-legend" />
      </div>
    </div>

    <div class="image-review__overlap-content">
      <div v-if="toggleShown" class="image-review__bg-toggle" data-testid="image-review-toggles">
        <ImageReviewBgToggle :bg-removed="bgRemoved" @change-bg="removeBackgroundChange" />
      </div>

      <div class="image-review__controls">
        <ImageReviewAsis
          v-if="asisShown"
          data-testid="image-review-asis"
          @clear-choose="clearChoose"
        />

        <ImageReviewEnhanced
          v-else-if="enhancedShown"
          data-testid="image-review-enhanced"
          :enhanced="enhanced"
          @use-as-is="useAsis"
          @use-enhancement="useEnhancement"
        />

        <ImageReviewEnhancing
          v-else-if="loadingShown"
          :status="loadingStatus"
          :percentage="loadingPercentage"
          data-testid="image-review-loading"
          @cancel="clearChoose"
        />

        <ImageReviewControls
          v-else
          data-testid="image-review-controls"
          @use-as-is="useAsis"
          @use-enhance="useEnhancement"
        />

        <ImageReviewRequest
          v-if="config.redraw_note_jiffymade"
          data-testid="image-review-request"
          class="image-review__request"
          :comment="comment"
          @save-comment="saveComment"
        />
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
@import "frontend/init/jiffy/mixin";
@import "frontend/init/pod/settings";

.image-review {
  $this: &;

  font-family: $pod-font-family;

  @include with-container(container, $pod-pdp-desktop-response) {
    display: grid;
    grid-template-rows: min-content 1fr auto;
    min-height: 100%;
  }

  &--disabled {
    opacity: 0.3;
    pointer-events: none;

    #{$this}__background-wrapper {
      display: none;

      @include with-container(container, $pod-pdp-desktop-response) {
        display: block;
      }
    }

    #{$this}__overlap-content {
      margin-top: 0;

      @include with-container(container, $pod-pdp-desktop-response) {
        margin-top: 16px;
      }
    }
  }

  &__title {
    font-size: 16px;
    font-weight: 500;
    margin-bottom: 10px;
    color: $black;
  }

  &__intro {
    margin-bottom: 12px;
  }

  &__intro-strong {
    font-weight: 600;
  }

  &__background-wrapper {
    position: relative;
    z-index: 0;
    padding-top: 100%;
    background-color: $pod-gray-modern-100;

    @include only-mobile {
      margin: 0 (-$indent-mobile-constructor);
    }
  }

  &__image {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    padding: 64px 0;

    @include with-container(container, $pod-pdp-desktop-response) {
      padding-bottom: 0;
    }
  }

  &__overlap-content {
    position: relative;
    z-index: 1;
    margin-top: -50px;

    @include with-container(container, $pod-pdp-desktop-response) {
      margin-top: 16px;
    }
  }

  &__bg-toggle {
    display: flex;
    justify-content: center;
  }

  &__titles {
    position: absolute;
    z-index: 1;
    top: 16px;
    left: 0;
    right: 0;
  }

  &__controls {
    margin-top: 13px;
    padding: 15px 18px 18px;
    border-radius: 8px;
    border: 1px solid $pod-gray-modern-200;
    background-color: $white;
    box-shadow: $pod-card-shadow;
  }

  &__request {
    margin-top: 20px;
  }
}
</style>
